import { Calendar, MapPin, CreditCard, Package } from "lucide-react";
import { OrderData, OrderItem } from "@nusrif/types";
import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";

interface OrderDetailsProps {
  orderData: OrderData;
}

export const OrderDetails = ({ orderData }: OrderDetailsProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentMethodName = (method: string) => {
    switch (method) {
      case "card":
        return "Credit/Debit Card";
      case "upi":
        return "UPI";
      case "wallet":
        return "Digital Wallet";
      case "cod":
        return "Cash on Delivery";
      default:
        return method;
    }
  };

  const getEstimatedDelivery = () => {
    const orderDate = new Date(orderData.orderDate);
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(deliveryDate.getDate() + 5); // Add 5 days
    return deliveryDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Order Info Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-primary-blue" />
            <span className="font-medium text-black">Order Date</span>
          </div>
          <p className="text-sm text-gray-600">
            {formatDate(orderData.orderDate)}
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-primary-blue" />
            <span className="font-medium text-black">Items</span>
          </div>
          <p className="text-sm text-gray-600">
            {orderData.summary.itemCount} item
            {orderData.summary.itemCount !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
      {/* Order Status */}
      <div className="grid grid-cols-1 gap-4">
        <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-primary-blue" />
            <span className="font-medium text-black">Order Status</span>
          </div>
          <p className="text-sm text-gray-600">
            {orderData.status === "PaymentSettled" ? "In Progress" : orderData.status}
          </p>
        </div>
      </div>
      {/* Delivery Information */}
      <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <MapPin className="w-5 h-5 text-primary-blue" />
          <span className="font-medium text-black">Delivery Address</span>
        </div>
        <div className="space-y-1">
          <p className="font-medium text-black">
            {orderData.shippingAddress?.fullName}
          </p>
          <p className="text-sm text-gray-600">
            {orderData.shippingAddress?.phoneNumber}
          </p>
          <p className="text-sm text-gray-700">
            {orderData.shippingAddress?.streetLine1}
            {orderData.shippingAddress?.streetLine2 &&
              `, ${orderData.shippingAddress.streetLine2}`}
          </p>
          <p className="text-sm text-gray-700">
            {orderData.shippingAddress?.city}, {orderData.shippingAddress?.province} -{" "}
            {orderData.shippingAddress?.postalCode}
          </p>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-sm text-success font-medium">
            Estimated Delivery: {getEstimatedDelivery()}
          </p>
        </div>
      </div>
      {/* Payment Information */}
      <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="w-5 h-5 text-primary-blue" />
          <span className="font-medium text-black">Payment Details</span>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Payment Method</span>
            <span className="text-sm font-medium text-black">
              {getPaymentMethodName(orderData.paymentMethod)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Total Amount</span>
            <span className="text-sm font-bold text-primary-blue">
              {formatCurrency(orderData.finalTotal, config.region.currencyCode)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Payment Status</span>
            <span className="text-sm font-medium text-success">
              {orderData.paymentMethod === "cod" ? "Pending" : "Completed"}
            </span>
          </div>
        </div>
      </div>
      {/* Order Items */}
      <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
        <h3 className="font-medium text-black mb-3">Order Items</h3>
        <div className="space-y-3">
          {orderData.items.map((item: OrderItem) => (
            <div key={item.id} className="flex items-center gap-3 py-2">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-xs font-medium text-gray-600">
                  {item.quantity}x
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-black text-sm">{item.name}</p>
                <p className="text-xs text-gray-600">{item.subtitle}</p>
                {(item.size || item.color) && (
                  <p className="text-xs text-gray-500">
                    {item.size && `Size: ${item.size}`}
                    {item.size && item.color && " • "}
                    {item.color && `Color: ${item.color}`}
                  </p>
                )}
              </div>
              <p className="font-medium text-black">
                {formatCurrency(item.price, config.region.currencyCode)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
